import {
	type AnyArityOneFunction,
	type ArityOneParameter,
} from '@post-office/shared-contracts/pipeline/constraints';

import {
	type AnyBackendMessageStage,
	type AnyBackendPlacementStage,
	type AnyPayload,
	type AnySyncBackendMessageStage,
	type PlacementBackendRuntimeResult,
	type RequestContext,
	type SyncRequestContext,
} from '../../types';

const HISTOGRAM_LATENCY_INTERVAL_BUCKETS: number[] = [25, 50, 100, 150, 200, 250, 500, 1000];

const timerName: string = 'post-office.placement-v2.stage.execute';

const counterName: string = 'post-office.placement-v2.stage.result';

const createTimedStage =
	<T extends (params: { request: SyncRequestContext }) => unknown>(
		tag?: ({
			input,
			output,
		}: {
			input: ArityOneParameter<T>;
			output?: Awaited<ReturnType<T>>;
		}) => AnyPayload,
	) =>
	(stage: T): T =>
		(async (params: ArityOneParameter<T>) => {
			params.request.logger.info('Started stage');

			const startTime = performance.now();

			const { stop } = params.request.metrics
				.histogram(timerName, HISTOGRAM_LATENCY_INTERVAL_BUCKETS)
				.measure();

			const result = stage(params);

			let tags: AnyPayload | undefined;

			try {
				const unwrappedResult = (result instanceof Promise ? await result : result) as Awaited<
					ReturnType<T>
				>;

				try {
					tags = tag?.({ input: params, output: unwrappedResult });
				} catch {
					params.request.logger.warn('Unable to apply tags to stage logs');
				}

				params.request.metrics.counter(counterName, { status: 'success' }).incr();

				return unwrappedResult;
			} catch (error) {
				params.request.metrics.counter(counterName, { status: 'error' }).incr();

				throw error;
			} finally {
				stop();

				const latencyInMs = performance.now() - startTime;
				// eslint-disable-next-line @atlassian/incredible/safe-logging-practices
				params.request.logger.info(
					{
						latencyInMs,
						...tags,
					},
					'Finished stage',
				);
			}
		}) as T;

const createTimedSystemStage =
	<T extends AnyArityOneFunction>(fn: (context: { request: SyncRequestContext }) => T) =>
	(contexts: { request: RequestContext }): T => {
		return (async (params: ArityOneParameter<T>) => {
			contexts.request.logger.info('Started stage');

			const startTime = performance.now();

			const { stop } = contexts.request.metrics
				.histogram(timerName, HISTOGRAM_LATENCY_INTERVAL_BUCKETS)
				.measure();

			const result = fn(contexts)(params);

			try {
				const unwrappedResult = (await result) as ReturnType<T>;

				contexts.request.metrics.counter(counterName, { status: 'success' }).incr();

				return unwrappedResult;
			} catch (error) {
				contexts.request.metrics.counter(counterName, { status: 'error' }).incr();

				throw error;
			} finally {
				stop();

				const latencyInMs = performance.now() - startTime;
				// eslint-disable-next-line @atlassian/incredible/safe-logging-practices
				contexts.request.logger.info({ latencyInMs }, 'Finished stage');
			}
		}) as T;
	};

export const messageTimedStage = createTimedStage<
	AnyBackendMessageStage | AnySyncBackendMessageStage
>();

export const placementTimedStage = createTimedStage<AnyBackendPlacementStage>(
	({ input, output }) => {
		if (!output) {
			return {};
		}

		const inputMessageTemplateIds = Array.from(
			new Set(input.data.messages.map((message) => message.messageTemplateId)).values(),
		);

		const outputMessageTemplateIds = Array.from(
			new Set(output.messages.map((message) => message.messageTemplateId)).values(),
		);

		const deltaMessageTemplateIds = inputMessageTemplateIds.filter(
			(id) => !outputMessageTemplateIds.includes(id),
		);

		const inputMessageInstanceIds = input.data.messages.map(
			({ messageInstanceId }) => messageInstanceId,
		);

		const outputMessageInstanceIds = output.messages.map(
			({ messageInstanceId }) => messageInstanceId,
		);

		const deltaMessageInstanceIds = inputMessageInstanceIds.filter(
			(id) => !outputMessageInstanceIds.includes(id),
		);

		return {
			input: {
				number: input.data.messages.length,
				messageTemplateIds: inputMessageTemplateIds,
				messageInstanceIds: inputMessageInstanceIds,
			},
			output: {
				number: output.messages.length,
				messageTemplateIds: outputMessageTemplateIds,
				messageInstanceIds: outputMessageInstanceIds,
			},
			delta: {
				number: output.messages.length - input.data.messages.length,
				messageTemplateIds: deltaMessageTemplateIds,
				messageInstanceIds: deltaMessageInstanceIds,
			},
		};
	},
);

export const placementRuntimeTimedStage = createTimedSystemStage;

export const logRuntimeResult =
	({ request }: { request: RequestContext }) =>
	(result: PlacementBackendRuntimeResult): void => {
		const errorMessageTemplateIds = Array.from(
			new Set(result.errors.map((e) => e.messageTemplateId)).values(),
		);

		const messageTemplateIds = Array.from(
			new Set(result.data.messages.map((e) => e.messageTemplateId)).values(),
		);

		const formattedMessages = result.data.messages.map(
			({
				messageTemplateId,
				messageInstanceId,
				messageCreationType,
				messageCategory,
				triggerId,
			}) => ({
				messageTemplateId,
				messageInstanceId,
				messageCreationType,
				messageCategory,
				triggerId,
			}),
		);

		const formattedErrorDetails = result.errors.map(
			({ messageTemplateId, messageInstanceId, reason }) => ({
				messageTemplateId,
				messageInstanceId,
				reason,
			}),
		);

		request.logger.info(
			{
				errors: {
					number: result.errors.length,
					messageTemplateIds: errorMessageTemplateIds,
					messageDetails: formattedErrorDetails,
				},
				messages: {
					number: result.data.messages.length,
					messageTemplateIds: messageTemplateIds,
					messageDetails: formattedMessages,
				},
			},
			'Runtime result',
		);
	};
